"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@acme/ui";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

import { authClient, useSession } from "~/lib/auth/better-auth-client";

/**
 * Anonymous sign-in. INTENTIONALLY UNLINKED — nothing in the app routes here
 * any more, so a "remove unreferenced route" sweep will read this as dead code.
 * It is not. Keep it.
 *
 * The UI entry points were removed (the "Try it now" block on /access and the
 * demo's "Try real version" CTA) because anonymous access converts at ~1.5-2%
 * while creating a user, an org and face photos per throwaway session. The
 * route itself stays reachable by direct URL so it can still be linked
 * deliberately — a campaign, a trade show, a support conversation.
 *
 * Before drawing conclusions from anonymous conversion numbers, note that data
 * before 2026-07-18 (50071fd7) is not usable: the attribution move in
 * onLinkAccount threw on every conversion, so those users left no trace and are
 * counted as failures.
 */
export default function AnonymousLogin() {
  const router = useRouter();
  const { data, isPending, error } = useSession();
  const t = useTranslations("auth");

  useEffect(() => {
    if (data?.user) {
      router.push("/");
      return;
    }

    if (!isPending && !error) {
      authClient.signIn.anonymous();
    }
  }, [isPending, data, error, router]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl">
            {t("anonymousAccess.title")}
          </CardTitle>
          <CardDescription>{t("anonymousAccess.description")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center space-y-4 pt-6">
          <div className="flex flex-col items-center space-y-2">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {t("anonymousAccess.loading")}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
